import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowRight } from 'lucide-react';
import { useMember } from '../access/MemberAuthContext';
import EnquiryMemberGate from './EnquiryMemberGate';
import { track } from '../lib/track';

const API = process.env.REACT_APP_BACKEND_URL;

const PATHWAYS = [
  { value: 'flight', label: 'Flight Academy' },
  { value: 'rail', label: 'Rail Academy' },
  { value: 'both', label: 'Both' },
];
const AGE_BANDS = [
  { value: '5-7', label: '5–7 years' },
  { value: '8-11', label: '8–11 years' },
  { value: '12-16', label: '12–16 years' },
];
const FORMATS = [
  { value: 'weekend', label: 'Weekend' },
  { value: 'school-holiday', label: 'School holiday' },
  { value: 'either', label: 'Either / flexible' },
];

/**
 * AcademyInterestForm — the ONLY Academy entry point in the public release.
 * Spec: no bookings, no payment, no fixed dates. Submission always ends with
 * "Thank you. Your interest has been registered; this is not a booking."
 */
const AcademyInterestForm = ({ defaultPathway }) => {
  const [form, setForm] = useState({
    pathway: defaultPathway || 'flight',
    ageBand: '8-11',
    format: 'either',
    area: '',
    name: '',
    email: '',
    message: '',
    marketingOptIn: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const { member } = useMember();

  useEffect(() => {
    if (!member) return;
    const fullName = [member.first_name, member.last_name].filter(Boolean).join(' ');
    setForm((f) => ({ ...f, name: f.name || fullName, email: member.email }));
  }, [member]);

  const update = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Please share your name so we can respond.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/academy/interest`, form, { withCredentials: true });
      track('form_submit', 'academy-family-interest');
      setDone(true);
      toast.success('Thank you. Your interest has been registered — this is not a booking.');
    } catch {
      toast.error('Something went wrong. Please try again or email hello@n3uro.world.');
    } finally {
      setSubmitting(false);
    }
  };

  if (member === null) {
    return <div className="service-enquiry-form" data-testid="enquiry-member-loading"><p className="service-enquiry-note">Checking your N3URO ACCESS session…</p></div>;
  }
  if (member === false) return <EnquiryMemberGate kind={'register your interest'} />;

  if (done) {
    return (
      <div className="service-enquiry-form service-enquiry-done" data-testid="academy-interest-done">
        <h3>Thank you.</h3>
        <p>
          Your interest has been registered. <strong>This is not a booking.</strong> You'll receive an
          acknowledgement by email and we'll be in touch when places open for your preferred pathway.
        </p>
      </div>
    );
  }

  return (
    <form className="service-enquiry-form" onSubmit={submit} data-track-form="academy-family-interest" data-testid="academy-interest-form" noValidate>
      <div className="service-enquiry-row">
        <label className="service-enquiry-field">
          <span>Pathway</span>
          <select value={form.pathway} onChange={update('pathway')} data-testid="field-pathway">
            {PATHWAYS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </label>
        <label className="service-enquiry-field">
          <span>Age band</span>
          <select value={form.ageBand} onChange={update('ageBand')} data-testid="field-age-band">
            {AGE_BANDS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </label>
      </div>

      <div className="service-enquiry-row">
        <label className="service-enquiry-field">
          <span>Format</span>
          <select value={form.format} onChange={update('format')} data-testid="field-format">
            {FORMATS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </label>
        <label className="service-enquiry-field">
          <span>Preferred area (optional)</span>
          <input type="text" placeholder="e.g. South-East England" value={form.area} onChange={update('area')} data-testid="field-area" />
        </label>
      </div>

      <div className="service-enquiry-row">
        <label className="service-enquiry-field">
          <span>Your name <span className="service-enquiry-req">*</span></span>
          <input type="text" required value={form.name} onChange={update('name')} data-testid="field-name" />
        </label>
        <label className="service-enquiry-field">
          <span>Email (from your account)</span>
          <input type="email" value={form.email} readOnly aria-readonly="true" title="Your N3URO ACCESS email" data-testid="field-email" />
        </label>
      </div>

      <label className="service-enquiry-field">
        <span>Message (optional)</span>
        <textarea rows={3} placeholder="Anything you'd like us to know. Please avoid diagnoses or sensitive information." value={form.message} onChange={update('message')} data-testid="field-message" />
      </label>

      <label className="service-enquiry-opt">
        <input type="checkbox" checked={form.marketingOptIn} onChange={update('marketingOptIn')} data-testid="field-marketing" />
        <span>I'm happy to hear about future N3URO Academy news by email.</span>
      </label>

      <p className="service-enquiry-note">
        This registers your interest. It is not a booking and no payment is taken.
      </p>

      <button type="submit" className="service-enquiry-submit" disabled={submitting} data-testid="academy-interest-submit">
        <span>{submitting ? 'Sending…' : 'Register interest'}</span>
        {!submitting && <ArrowRight size={16} strokeWidth={2.4} />}
      </button>
    </form>
  );
};

export default AcademyInterestForm;
